import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';
import { AlertTriangle, X, Trash2 } from 'lucide-react';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import type { Post } from '../../../types';
import { useLanguage } from '../../../contexts/LanguageContext';

interface PostDeleteConfirmModalProps {
  post: Post;
  onClose: () => void;
  onDelete: (postId: string) => void;
}

export function PostDeleteConfirmModal({ post, onClose, onDelete }: PostDeleteConfirmModalProps) {
  const { t } = useLanguage();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(post.id);
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md backdrop-blur-sm" style={{ backgroundColor: 'rgba(var(--color-surface-rgb, 255, 255, 255), 0.9)' }}>
        <DialogTitle className="sr-only">{t.posts.deleteConfirm}</DialogTitle>
        <DialogDescription className="sr-only">
          {t.posts.deleteConfirmDesc}
        </DialogDescription>
        
        <div className="flex flex-col items-center gap-4 py-6 px-6">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-danger-light)' }}>
            <AlertTriangle className="w-6 h-6" style={{ color: 'var(--color-danger)' }} />
          </div>
          
          <div className="text-center">
            <h3 className="mb-2" style={{ color: 'var(--color-text-primary)' }}>{t.posts.deleteConfirm}</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {t.posts.deleteConfirmDesc}
            </p>
          </div>
          
          <div className="flex gap-3 justify-center mt-2">
            <AnimatedIconButton
              icon={X}
              label={t.common.cancel}
              onClick={onClose}
              variant="secondary"
              revealLabelOnHover={false}
            />
            <AnimatedIconButton
              icon={Trash2}
              label={t.common.delete}
              onClick={handleDelete}
              variant="danger"
              revealLabelOnHover={false}
              disabled={isDeleting}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}